import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowRight, ExternalLink, ShoppingCart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  arrow: ArrowRight,
  external: ExternalLink,
  cart: ShoppingCart,
};

const STYLE_PRESETS = {
  banner: 'w-full py-4 px-6 flex items-center justify-between rounded-lg',
  inline: 'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm',
  sidebar: 'w-full p-5 rounded-lg text-center',
  sticky: 'fixed bottom-4 right-4 z-40 px-5 py-3 rounded-full shadow-xl flex items-center gap-2',
};

const COLOR_PRESETS = {
  neon: 'bg-[#e8ff00] text-black hover:bg-[#d4eb00]',
  red: 'bg-[#E63946] text-white hover:bg-[#d32f3e]',
  teal: 'bg-[#06B6D4] text-white hover:bg-[#0891b2]',
  dark: 'bg-zinc-900 text-white border border-zinc-700 hover:border-[#e8ff00]',
  gradient: 'bg-gradient-to-r from-[#E63946] to-[#e8ff00] text-black hover:opacity-90',
};

export default function ABTestCTA({ articleSlug }) {
  const [variant, setVariant] = useState(null);
  const [testId, setTestId] = useState(null);
  const impressionTracked = useRef(false);

  useEffect(() => {
    // Check for cached variant in sessionStorage
    const cacheKey = `ab_${articleSlug || 'global'}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setVariant(parsed.variant);
        setTestId(parsed.test_id);
        return;
      } catch { /* ignore */ }
    }

    // Fetch assigned variant
    fetch(`${API_URL}/api/ab-test/variant?article_slug=${encodeURIComponent(articleSlug || '')}`)
      .then(r => r.json())
      .then(data => {
        if (data.variant) {
          setVariant(data.variant);
          setTestId(data.test_id);
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
        }
      })
      .catch(() => {});
  }, [articleSlug]);

  // Track impression once
  useEffect(() => {
    if (!variant || !testId || impressionTracked.current) return;
    impressionTracked.current = true;
    fetch(`${API_URL}/api/ab-test/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        test_id: testId,
        variant_id: variant.variant_id,
        event: 'impression',
        article_slug: articleSlug || '',
      }),
    }).catch(() => {});
  }, [variant, testId, articleSlug]);

  const handleClick = useCallback(() => {
    if (!variant || !testId) return;
    fetch(`${API_URL}/api/ab-test/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        test_id: testId,
        variant_id: variant.variant_id,
        event: 'click',
        article_slug: articleSlug || '',
      }),
    }).catch(() => {});
  }, [variant, testId, articleSlug]);

  if (!variant) return null;

  const Icon = ICON_MAP[variant.icon] || ArrowRight;
  const styleClass = STYLE_PRESETS[variant.placement] || STYLE_PRESETS.inline;
  const colorClass = COLOR_PRESETS[variant.color] || COLOR_PRESETS.neon;
  const href = variant.cta_url || '#';

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={`${styleClass} ${colorClass} transition-all duration-200 cursor-pointer no-underline`}
      data-testid={`ab-cta-${variant.variant_id}`}
      style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.05em' }}
    >
      {variant.placement === 'banner' ? (
        <>
          <div>
            <p className="text-xs opacity-70 uppercase tracking-wider">{variant.subtitle || 'Recommended'}</p>
            <p className="text-lg font-bold">{variant.cta_text}</p>
          </div>
          <Icon className="w-5 h-5 flex-shrink-0" />
        </>
      ) : (
        <>
          {variant.cta_text}
          <Icon className="w-4 h-4" />
        </>
      )}
    </a>
  );
}
